import { decodeImageData } from './listingsTransport';

/**
 * Extracts the first valid (non-nophoto) image URL from a listing image source.
 * Accepts whatever `listings.cfc` sends — a JSON string, an array of
 * objects/strings, or a single record / position-keyed struct as a JSON object.
 * Used as a fallback image resolver in SingleListing and MultipleListings blocks,
 * checking listing.image_data and listing.custom_image_data in that order.
 *
 * Shares `decodeImageData` with the transport rather than parsing again: two
 * parsers meant a shape one accepted and the other rejected showed images in
 * some blocks and the placeholder in others.
 */
export const getListingImageUrl = (imageSource: unknown): string | undefined =>
	decodeImageData(imageSource)?.find(url => !url.includes('nophoto'));

/**
 * The thumbnail for a raw CFC listing row: the pre-resolved path if the feed
 * gave one, otherwise the first usable image off the row's own records.
 *
 * Every picker needs this and each had grown its own copy, which is how the
 * section/column drawer ended up on `parsed?.[0]?.url` — no object support, no
 * position sort, no `custom_image_data` — while the wrapper's drawer worked.
 */
/**
 * Every usable image on a listing, thumbnail first.
 *
 * The pickers used to store `image_data: [thumbnail]` — a single-element array —
 * which silently killed `{{listing.image_data_1}}` through `_9`. Only Image 1
 * ever resolved, on every block, because the other nine indexes did not exist on
 * the row the picker cached.
 *
 * Order is the listing's own, untouched. `image_data[1]` has to stay Image 2 —
 * prepending a thumbnail that came from `imagepath` shifts every index by one
 * and silently renumbers all ten tags.
 *
 * The pre-resolved `imagepath` is used only when the listing carries no photo
 * records at all, so a drawer reading index 0 still has something to show.
 */
export const resolveListingImages = (
	listing: Record<string, any> | null | undefined,
): string[] => {
	const decoded =
		decodeImageData(listing?.image_data) ??
		decodeImageData(listing?.custom_image_data) ??
		[];
	const usable = decoded.filter(url => url && !url.includes('nophoto'));
	if (usable.length) return usable;

	const thumbnail = resolveListingThumbnail(listing);
	return thumbnail ? [thumbnail] : [];
};

export const resolveListingThumbnail = (
	listing: Record<string, any> | null | undefined,
): string | undefined => {
	const direct = [listing?.imagepath, listing?.image_path].find(
		(url: unknown): url is string =>
			typeof url === 'string' && url.trim() !== '' && !url.includes('nophoto'),
	);
	if (direct) return direct;

	return (
		getListingImageUrl(listing?.image_data) ??
		getListingImageUrl(listing?.custom_image_data)
	);
};
